import { DefaultChatTransport, type FileUIPart, type UIMessage } from 'ai'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowUp,
  Clock,
  File as FileIcon,
  HelpCircle,
  Paperclip,
  Plus,
  ThumbsDown,
  ThumbsUp,
} from 'react-feather'

import { requireSessionToken } from '@avantstay/auth-token'
import { useDragResize } from '@avantstay/backoffice-core'
import { IconGlitter } from '@avantstay/backoffice-vectors'

import { useChat } from '@ai-sdk/react'

import { ApprovalCard, type ApprovalDecision, type RemyApprovalData } from './ApprovalCard'
import * as P from './AskAiChatSidePanel.styles'
import { AttachmentChips } from './AttachmentChips'
import { AssistantContent } from './ChatMarkdown'
import * as S from './RemyChat.styles'
import { RemyGuide } from './RemyGuide'
import { downloadRemyOutputFile, readRemyOutputFile, type RemyOutputFile } from './remyOutputFiles'
import { REMY_API, type RemyFeedbackRating, sendRemyFeedback } from './remySessions'
import { RemyStep, toActivitySteps } from './remySteps'
import { type ChatAttachment, useFileAttachments } from './useFileAttachments'
import { useFileDropZone } from './useFileDropZone'

const fileToDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error(file.name))
    reader.readAsDataURL(file)
  })

// The Remy gateway ingests attachments as inline base64 data-URL file parts of
// the chat POST — no separate upload endpoint. Files with no browser-known MIME
// (e.g. .sql) are sent as octet-stream; the backend classifies those by
// extension.
const toFileParts = (attachments: ChatAttachment[]): Promise<FileUIPart[]> =>
  Promise.all(
    attachments.map(
      async ({ file }): Promise<FileUIPart> => ({
        type: 'file',
        mediaType: file.type || 'application/octet-stream',
        filename: file.name,
        url: await fileToDataUrl(file),
      }),
    ),
  )

// Sent-file payloads stay in message state as base64 data URLs for the life of
// this mount (dropSentFilePayloads blanks only the request copies), so those
// chips can offer a re-download. Resumed history arrives without payloads, so
// anything else renders as a plain, non-clickable chip.
const isDownloadablePart = (part: FileUIPart) =>
  part.url.startsWith('data:') && part.url.includes(';base64,')

// Decode to a Blob and download via a temporary object URL instead of linking
// the data URL directly — Safari mishandles data-URL downloads and Chrome caps
// navigable data-URL length below our 10 MB attachment cap.
const downloadFilePart = (part: FileUIPart) => {
  const base64 = part.url.slice(part.url.indexOf(',') + 1)
  const bytes = Uint8Array.from(atob(base64), char => char.charCodeAt(0))
  const objectUrl = URL.createObjectURL(new Blob([bytes], { type: part.mediaType }))
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = part.filename ?? 'attachment'
  anchor.click()
  // Deferred: Firefox can drop the download if the URL is revoked mid-click.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

// useChat re-sends the whole history on every POST, so without this each past
// attachment's base64 payload would ride along on every later turn until the
// body outgrows the server cap. The server only ingests the LAST user
// message's files (earlier ones are already stored server-side), so blank out
// the data URLs everywhere else and keep filename/mediaType for local display.
const dropSentFilePayloads = (messages: UIMessage[]): UIMessage[] =>
  messages.map((message, index) =>
    index === messages.length - 1
      ? message
      : {
          ...message,
          parts: message.parts.map(part =>
            part.type === 'file' && part.url.startsWith('data:') ? { ...part, url: '' } : part,
          ),
        },
  )

interface RemyChatProps {
  // The session/thread id — sent as the chat `id` so the server continues the
  // right LangGraph thread. Parent keys this component by it, so switching
  // sessions remounts the chat with fresh state.
  sessionId: string
  // History to hydrate the UI when resuming; undefined for a brand-new chat.
  initialMessages?: UIMessage[]
  onNewChat: () => void
  onBrowseHistory: () => void
  canBrowseHistory: boolean
}

export const RemyChat = ({
  sessionId,
  initialMessages,
  onNewChat,
  onBrowseHistory,
  canBrowseHistory,
}: RemyChatProps) => {
  const [input, setInput] = useState('')
  const [steps, setSteps] = useState<RemyStep[]>([])
  // Decisions taken in this mount, keyed by approvalId — flips the pending
  // card into its resolved state without waiting for a server round-trip.
  const [approvalDecisions, setApprovalDecisions] = useState<Record<string, ApprovalDecision>>({})
  // Ratings sent in this mount, keyed by message id — freezes the thumbs into
  // their selected state without a server round-trip (ratings are one-shot).
  const [messageFeedback, setMessageFeedback] = useState<Record<string, RemyFeedbackRating>>({})
  const [feedbackError, setFeedbackError] = useState<string | null>(null)
  const [outputFileError, setOutputFileError] = useState<string | null>(null)
  const [showGuide, setShowGuide] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const {
    attachments,
    error: attachmentError,
    addFiles,
    removeAttachment,
    clearAttachments,
  } = useFileAttachments()
  const { isDragActive, dropHandlers } = useFileDropZone(addFiles)

  // Drag the strip above the input bar to grow the textarea; the height is
  // persisted, so it survives the per-session remounts of this component.
  const inputAreaRef = useRef<HTMLTextAreaElement>(null)
  const {
    size: inputHeight,
    isResizing: isResizingInput,
    handleProps: inputResizeProps,
  } = useDragResize({
    axis: 'y',
    min: 48,
    max: 400,
    storageKey: 'remyChat.inputHeight',
    getTarget: () => inputAreaRef.current,
  })

  // Attach the session token per request via `requireSessionToken()`, which
  // throws rather than letting a request go out unauthenticated. Overriding
  // `fetch` (rather than `headers`) is the cross-version-stable path from the
  // integration doc.
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: REMY_API,
        prepareSendMessagesRequest: ({ id, messages, trigger, messageId, body }) => ({
          // Keep everything the default transport would send; only the
          // messages array is rewritten.
          body: { ...body, id, messages: dropSentFilePayloads(messages), trigger, messageId },
        }),
        fetch: async (url, options) => {
          const token = await requireSessionToken()
          return fetch(url, {
            ...options,
            headers: {
              ...options?.headers,
              Authorization: `Bearer ${token}`,
            },
          })
        },
      }),
    [],
  )

  const { messages, sendMessage, status, error } = useChat({
    id: sessionId, // continues this server-side thread
    messages: initialMessages, // hydrates the UI when resuming
    transport,
    onData: part => {
      // Transient progress steps arrive here and are never persisted to messages.
      if (part.type === 'data-remy-step') {
        setSteps(prev => [...prev, part.data as RemyStep])
      }
    },
  })

  const textOf = (message: (typeof messages)[number]) =>
    message.parts
      .filter(part => part.type === 'text')
      .map(part => ('text' in part ? part.text : ''))
      .join('')

  const busy = status === 'submitted' || status === 'streaming'
  const lastMessage = messages[messages.length - 1]
  const lastAssistantText = lastMessage?.role === 'assistant' ? textOf(lastMessage).trim() : ''
  // Show the progress bubble while Remy works AND nothing readable has landed
  // yet — i.e. before the assistant turn starts, or after it starts but is still
  // an empty shell with no text. This avoids a blank assistant bubble.
  const showProgress = busy && lastAssistantText.length === 0
  // toActivitySteps always yields at least a live "Thinking…" row, so the
  // progress bubble is never empty.
  const activitySteps = useMemo(() => toActivitySteps(steps), [steps])

  // Scroll to the latest message — also on mount, so resumed history lands at the
  // bottom on the last exchange.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, steps, showProgress])

  // Re-entrancy guard: encoding attachments is async, and a second Enter during
  // that window would double-send (`busy` only flips after sendMessage).
  const sendingRef = useRef(false)
  const [sendError, setSendError] = useState<string | null>(null)

  const handleSend = async () => {
    const text = input.trim()
    if (!text || busy || sendingRef.current) return
    sendingRef.current = true
    try {
      let files: FileUIPart[] = []
      if (attachments.length > 0) {
        try {
          files = await toFileParts(attachments)
        } catch (e) {
          // Keep the staged chips so the user can drop the broken one and retry.
          const name = e instanceof Error && e.message ? e.message : 'one of the attached files'
          setSendError(`Couldn't read ${name}. Remove it and try again.`)
          return
        }
      }
      setSendError(null)
      setSteps([]) // reset per-turn progress
      sendMessage(files.length > 0 ? { text, files } : { text })
      clearAttachments()
      setInput('')
    } finally {
      sendingRef.current = false
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  // Approve/Reject on a pending approval card. Rides the regular chat POST
  // with an `approvalDecision` body field: the server resumes the paused turn
  // and streams the continuation like any other assistant message.
  const handleApprovalDecision = (approvalId: string, decision: ApprovalDecision) => {
    if (busy) return
    setApprovalDecisions(prev => ({ ...prev, [approvalId]: decision }))
    setSteps([])
    sendMessage(
      { text: decision === 'approve' ? 'Approve' : 'Reject' },
      { body: { approvalDecision: { approvalId, decision } } },
    )
  }

  // Helpful/not-helpful on an assistant answer. Optimistic: the thumbs freeze
  // immediately; a failed POST unfreezes them so the user can retry. Live
  // answers carry their execution id as a persistent `data-remy-execution`
  // part; resumed history doesn't, so the server falls back to chat+message id.
  const handleFeedback = (message: UIMessage, rating: RemyFeedbackRating) => {
    if (messageFeedback[message.id]) return
    setMessageFeedback(prev => ({ ...prev, [message.id]: rating }))
    setFeedbackError(null)
    const executionPart = message.parts.find(part => part.type === 'data-remy-execution') as
      | { data?: { executionId?: string } }
      | undefined
    const executionId = executionPart?.data?.executionId
    void sendRemyFeedback({
      rating,
      chatId: sessionId,
      messageId: message.id,
      ...(executionId ? { executionId } : {}),
    }).catch(() => {
      setMessageFeedback(prev => {
        const { [message.id]: _dropped, ...rest } = prev
        return rest
      })
      setFeedbackError("Couldn't send your feedback. Please try again.")
    })
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files)
    // Reset so a file that was attached and removed can be re-selected.
    e.target.value = ''
  }

  const handleOutputDownload = (file: RemyOutputFile) => {
    setOutputFileError(null)
    void downloadRemyOutputFile(file).catch(() => {
      setOutputFileError(`Couldn't download ${file.filename}. The file may have expired.`)
    })
  }

  return (
    <S.Root {...dropHandlers}>
      {isDragActive && <S.DropOverlay>Drop files to attach</S.DropOverlay>}
      {showGuide && <RemyGuide onClose={() => setShowGuide(false)} />}
      <S.ChatToolbar>
        <S.ToolbarButton type="button" onClick={onNewChat}>
          <Plus size={14} />
          New chat
        </S.ToolbarButton>
        {canBrowseHistory && (
          <S.ToolbarButton type="button" onClick={onBrowseHistory}>
            <Clock size={14} />
            Past chats
          </S.ToolbarButton>
        )}
        <S.ToolbarIconButton
          type="button"
          onClick={() => setShowGuide(true)}
          aria-label="Open user guide"
          title="User guide"
        >
          <HelpCircle size={14} />
        </S.ToolbarIconButton>
      </S.ChatToolbar>

      <S.MessageList ref={scrollRef}>
        {messages.length === 0 && !busy && (
          <S.EmptyState>
            <P.IconBadge>
              <IconGlitter width={28} height={28} />
            </P.IconBadge>
            <P.PanelTitle>Ask Remy anything about Voyage</P.PanelTitle>
            <P.PanelText>
              Get help with workflows, definitions, and how things work in the backoffice.
            </P.PanelText>
          </S.EmptyState>
        )}

        {messages.map(message => {
          const role = message.role === 'user' ? 'human' : 'assistant'
          const text = textOf(message)
          const sentFiles = message.parts.filter(part => part.type === 'file')
          const outputFiles = message.parts
            .filter(part => part.type === 'data-remy-file')
            .map(part => readRemyOutputFile((part as { data?: unknown }).data))
            .filter((file): file is RemyOutputFile => file !== undefined)
          const approvals = message.parts
            .filter(part => part.type === 'data-remy-approval')
            .map(part => (part as { data: RemyApprovalData }).data)
          // An assistant message can briefly exist with no text yet — the
          // progress bubble covers that gap, so don't render an empty shell.
          if (
            role === 'assistant' &&
            !text.trim() &&
            approvals.length === 0 &&
            outputFiles.length === 0
          )
            return null
          // Approval messages render as a single tinted card instead of the
          // regular bubble — the card headline already carries the message.
          if (approvals.length > 0) {
            return (
              <S.MessageRow key={message.id} $role="assistant">
                <S.Avatar>
                  <IconGlitter width={16} height={16} />
                </S.Avatar>
                <S.ApprovalStack>
                  {approvals.map(approval => (
                    <ApprovalCard
                      key={approval.approvalId}
                      approval={approval}
                      decided={approvalDecisions[approval.approvalId]}
                      busy={busy}
                      onDecide={handleApprovalDecision}
                    />
                  ))}
                  {outputFiles.map(file => (
                    <S.BubbleFileButton
                      key={file.id}
                      type="button"
                      onClick={() => handleOutputDownload(file)}
                      aria-label={`Download ${file.filename}`}
                      title={file.title ?? file.filename}
                    >
                      <FileIcon size={12} />
                      {file.title ?? file.filename}
                    </S.BubbleFileButton>
                  ))}
                </S.ApprovalStack>
              </S.MessageRow>
            )
          }
          const bubble = (
            <S.Bubble $role={role}>
              {role === 'assistant' ? <AssistantContent text={text} /> : text}
              {sentFiles.length > 0 && (
                <S.BubbleFileList>
                  {sentFiles.map((part, i) => {
                    const name = part.filename ?? 'attachment'
                    const key = `${part.filename ?? 'file'}-${i}`
                    return isDownloadablePart(part) ? (
                      <S.BubbleFileButton
                        key={key}
                        type="button"
                        onClick={() => downloadFilePart(part)}
                        aria-label={`Download ${name}`}
                      >
                        <FileIcon size={12} />
                        {name}
                      </S.BubbleFileButton>
                    ) : (
                      <S.BubbleFile key={key}>
                        <FileIcon size={12} />
                        {name}
                      </S.BubbleFile>
                    )
                  })}
                </S.BubbleFileList>
              )}
              {outputFiles.length > 0 && (
                <S.BubbleFileList>
                  {outputFiles.map(file => (
                    <S.BubbleFileButton
                      key={file.id}
                      type="button"
                      onClick={() => handleOutputDownload(file)}
                      aria-label={`Download ${file.filename}`}
                      title={file.title ?? file.filename}
                    >
                      <FileIcon size={12} />
                      {file.title ?? file.filename}
                    </S.BubbleFileButton>
                  ))}
                </S.BubbleFileList>
              )}
            </S.Bubble>
          )
          if (role !== 'assistant') {
            return (
              <S.MessageRow key={message.id} $role={role}>
                {bubble}
              </S.MessageRow>
            )
          }
          // Thumbs stay hidden while this message may still be streaming in.
          const rated = messageFeedback[message.id]
          const showFeedback = text.trim().length > 0 && (!busy || message.id !== lastMessage?.id)
          return (
            <S.MessageRow key={message.id} $role={role}>
              <S.Avatar>
                <IconGlitter width={16} height={16} />
              </S.Avatar>
              <S.AssistantBubbleGroup>
                {bubble}
                {showFeedback && (
                  <S.FeedbackRow>
                    <S.FeedbackButton
                      type="button"
                      $selected={rated === 'positive'}
                      disabled={rated !== undefined}
                      onClick={() => handleFeedback(message, 'positive')}
                      aria-label="Mark response as helpful"
                      title="Helpful"
                    >
                      <ThumbsUp size={13} />
                    </S.FeedbackButton>
                    <S.FeedbackButton
                      type="button"
                      $selected={rated === 'negative'}
                      disabled={rated !== undefined}
                      onClick={() => handleFeedback(message, 'negative')}
                      aria-label="Mark response as not helpful"
                      title="Not helpful"
                    >
                      <ThumbsDown size={13} />
                    </S.FeedbackButton>
                  </S.FeedbackRow>
                )}
              </S.AssistantBubbleGroup>
            </S.MessageRow>
          )
        })}

        {showProgress && (
          <S.MessageRow $role="assistant">
            <S.Avatar>
              <IconGlitter width={16} height={16} />
            </S.Avatar>
            <S.Bubble $role="assistant" $streaming>
              <S.StepList aria-label="Remy progress">
                {activitySteps.map((step, i) => (
                  <S.StepItem key={`${step.label}-${i}`} $active={step.active}>
                    {step.label}
                    {step.active ? '…' : ''}
                  </S.StepItem>
                ))}
              </S.StepList>
              <S.Sheen />
            </S.Bubble>
          </S.MessageRow>
        )}
      </S.MessageList>

      {error && (
        <S.ErrorBanner role="alert">
          <strong>Couldn&apos;t reach Remy.</strong> {error.message}
        </S.ErrorBanner>
      )}
      {feedbackError && <S.ErrorBanner role="alert">{feedbackError}</S.ErrorBanner>}
      {outputFileError && <S.ErrorBanner role="alert">{outputFileError}</S.ErrorBanner>}

      <S.InputBar>
        <S.InputResizeHandle
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize message box"
          $active={isResizingInput}
          {...inputResizeProps}
        />
        <S.InputWrapper $disabled={busy}>
          {attachments.length > 0 && (
            <AttachmentChips attachments={attachments} onRemove={removeAttachment} />
          )}
          {attachmentError && <S.AttachmentError role="alert">{attachmentError}</S.AttachmentError>}
          {sendError && <S.AttachmentError role="alert">{sendError}</S.AttachmentError>}
          <S.InputRow>
            <S.InputArea
              ref={inputAreaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Remy anything…"
              rows={2}
              style={inputHeight === undefined ? undefined : { height: inputHeight }}
            />
            <S.AttachButton
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach files"
            >
              <Paperclip size={16} />
            </S.AttachButton>
            <S.SendButton
              onClick={() => void handleSend()}
              disabled={!input.trim() || busy}
              aria-label="Send"
            >
              <ArrowUp size={16} />
            </S.SendButton>
          </S.InputRow>
          <S.HiddenFileInput
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileInputChange}
            aria-label="File attachments"
          />
        </S.InputWrapper>
      </S.InputBar>
    </S.Root>
  )
}
